// ===========================================
// Individual Booking Tab
// ===========================================
// Tab content for booking individual sessions from hybrid lessons

import { useState, useMemo } from 'react';
import { format, isBefore, startOfDay } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Event as EventIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import SlotPicker from './SlotPicker';
import {
  useMyBookings,
  useMyHybridLessons,
  useAvailableSlots,
  useOpenWeekSlots,
  useCreateBooking,
  useRescheduleBooking,
  useCancelBooking,
} from '../../hooks/useHybridBooking';
import {
  HybridBooking,
  TimeSlot,
  getBookingStatusColor,
  formatTimeSlot,
  canModifyBooking,
  getHoursUntilBooking,
} from '../../api/hybridBooking.api';
import { Lesson } from '../../api/lessons.api';
import {
  effectiveStartWeek,
  formatTermWeekDate,
  lessonWeekWindow,
  parseUTCDate,
  termWeekAnchor,
  termWeekCount,
} from '../../utils/dateUtils';

// ===========================================
// TYPES
// ===========================================

interface BookingModalState {
  open: boolean;
  lesson: Lesson | null;
  /** The clinic week the picker is opened for. The actual booked week is decided
      by the slot the parent picks (a clinic-week slot vs an overflow N+1 slot). */
  weekNumber: number | null;
  studentId: string | null;
}

interface RescheduleModalState {
  open: boolean;
  booking: HybridBooking | null;
}

interface CancelModalState {
  open: boolean;
  booking: HybridBooking | null;
  reason: string;
}

// ===========================================
// COMPONENT PROPS
// ===========================================

interface IndividualBookingTabProps {
  hideBookingsList?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function IndividualBookingTab({ hideBookingsList = false }: IndividualBookingTabProps) {
  // State
  const [bookingModal, setBookingModal] = useState<BookingModalState>({
    open: false,
    lesson: null,
    weekNumber: null,
    studentId: null,
  });
  const [rescheduleModal, setRescheduleModal] = useState<RescheduleModalState>({
    open: false,
    booking: null,
  });
  const [cancelModal, setCancelModal] = useState<CancelModalState>({
    open: false,
    booking: null,
    reason: '',
  });
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  // Queries
  const { data: myBookings, isLoading: bookingsLoading } = useMyBookings();
  const { data: hybridLessons = [], isLoading: lessonsLoading } = useMyHybridLessons();

  // Available slots query (when booking or reschedule modal is open).
  // The reschedule booking carries its `Lesson` relation (PascalCase — the API
  // sends no lowercase alias, #204). Fall back to the lessons we
  // already loaded so the slot query gets a real lessonId.
  const rescheduleBookingRecord = rescheduleModal.booking;
  const rescheduleLessonFromBooking =
    rescheduleBookingRecord?.Lesson;
  const rescheduleLessonFromList = rescheduleBookingRecord
    ? hybridLessons.find((l) => l.id === rescheduleBookingRecord.lessonId)
    : undefined;
  const activeLesson =
    bookingModal.lesson || rescheduleLessonFromBooking || rescheduleLessonFromList;
  const activeLessonId =
    activeLesson?.id || rescheduleBookingRecord?.lessonId || '';
  const activeWeek = bookingModal.weekNumber || rescheduleModal.booking?.weekNumber;
  // Is a booking's CLINIC WEEK opened to any week by the admin? Derived from
  // the lesson pattern's openClinicWeeks (the clinic week is open, not the
  // individual booking).
  const isClinicWeekOpen = (booking: HybridBooking): boolean => {
    const bookingLesson =
      booking.Lesson ?? hybridLessons.find((l) => l.id === booking.lessonId);
    const openWeeks = (bookingLesson?.HybridLessonPattern?.openClinicWeeks ?? []) as number[];
    return openWeeks.includes(booking.clinicWeekNumber ?? booking.weekNumber);
  };

  // A booking whose clinic week is OPEN is rescheduled from the
  // all-remaining-weeks slot list instead of the single-week list; the week
  // query is disabled while that mode is active.
  const isOpenWeekReschedule =
    rescheduleModal.open && !!rescheduleBookingRecord && isClinicWeekOpen(rescheduleBookingRecord);
  const { data: weekSlots, isLoading: weekSlotsLoading } = useAvailableSlots(
    isOpenWeekReschedule ? '' : activeLessonId,
    activeWeek || 0
  );
  const { data: openWeekSlots, isLoading: openWeekSlotsLoading } = useOpenWeekSlots(
    rescheduleBookingRecord?.id,
    isOpenWeekReschedule
  );
  const availableSlots = isOpenWeekReschedule ? openWeekSlots : weekSlots;
  const slotsLoading = isOpenWeekReschedule ? openWeekSlotsLoading : weekSlotsLoading;

  // Mutations
  const createBooking = useCreateBooking();
  const rescheduleBooking = useRescheduleBooking();
  const cancelBooking = useCancelBooking();

  // Group bookings by lesson
  const bookingsByLesson = useMemo(() => {
    const grouped: Record<string, HybridBooking[]> = {};
    (myBookings ?? []).forEach((booking) => {
      if (!grouped[booking.lessonId]) {
        grouped[booking.lessonId] = [];
      }
      grouped[booking.lessonId].push(booking);
    });
    return grouped;
  }, [myBookings]);

  // Handlers
  const handleOpenBookingModal = (
    lesson: Lesson,
    weekNumber: number,
    studentId: string
  ) => {
    // The picker is always opened for the clinic week. When overflow is open the
    // slot list also includes the following week's slots; which week a booking
    // lands in is decided by the slot the parent picks (see handleConfirmBooking).
    setBookingModal({
      open: true,
      lesson,
      weekNumber,
      studentId,
    });
    setSelectedSlot(null);
  };

  const handleCloseBookingModal = () => {
    setBookingModal({
      open: false,
      lesson: null,
      weekNumber: null,
      studentId: null,
    });
    setSelectedSlot(null);
  };

  const handleConfirmBooking = () => {
    if (!bookingModal.lesson || !bookingModal.studentId || !selectedSlot) {
      return;
    }

    // The chosen slot decides the week: a clinic-week slot books normally; an
    // overflow slot (weekNumber = clinic + 1) or an OPEN-clinic slot (any term
    // week) carries clinicWeekNumber so the server stores the physical week
    // with the clinic identity pinned.
    const slotWeek = selectedSlot.weekNumber;
    const slotClinicWeek = selectedSlot.clinicWeekNumber ?? selectedSlot.weekNumber;
    const weekMismatch = slotWeek !== slotClinicWeek;
    const dateKey =
      selectedSlot.dateKey ??
      (selectedSlot.date ? new Date(selectedSlot.date).toISOString().split('T')[0] : '');
    createBooking.mutate(
      {
        lessonId: bookingModal.lesson.id,
        studentId: bookingModal.studentId,
        weekNumber: slotWeek,
        // Send clinicWeekNumber whenever the slot lands outside its clinic
        // week (overflow N+1, or an open clinic placed anywhere in the term);
        // same-week bookings let the server infer it.
        ...(weekMismatch ? { clinicWeekNumber: slotClinicWeek } : {}),
        scheduledDate: `${dateKey}T00:00:00.000Z`,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
      },
      {
        onSuccess: () => {
          handleCloseBookingModal();
        },
      }
    );
  };

  const handleOpenRescheduleModal = (booking: HybridBooking) => {
    setRescheduleModal({ open: true, booking });
    setSelectedSlot(null);
  };

  const handleCloseRescheduleModal = () => {
    setRescheduleModal({ open: false, booking: null });
    setSelectedSlot(null);
  };

  const handleOpenCancelModal = (booking: HybridBooking) => {
    setCancelModal({ open: true, booking, reason: '' });
  };

  const handleCloseCancelModal = () => {
    setCancelModal({ open: false, booking: null, reason: '' });
  };

  const handleConfirmCancel = () => {
    if (!cancelModal.booking) return;

    cancelBooking.mutate(
      {
        id: cancelModal.booking.id,
        reason: cancelModal.reason || undefined,
      },
      {
        onSuccess: () => {
          handleCloseCancelModal();
        },
      }
    );
  };

  // Loading state
  const isLoading = bookingsLoading || lessonsLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {hybridLessons.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No hybrid lessons available for booking. Your child must be enrolled in a
          hybrid lesson to book individual sessions.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Hybrid Lessons Section */}
        <Grid item xs={12} md={hideBookingsList ? 12 : 8}>
          <Typography variant="h6" gutterBottom>
            Hybrid Lessons
          </Typography>

          {hybridLessons.map((lesson) => {
            const pattern = lesson.HybridLessonPattern || lesson.HybridPattern;
            if (!pattern) return null;

            const individualWeeks = (pattern.individualWeeks ?? []) as number[];
            const groupWeeks = (pattern.groupWeeks ?? []) as number[];
            const totalWeeks = Math.max(...individualWeeks, ...groupWeeks, 0);
            const lessonBookings = bookingsByLesson[lesson.id] || [];
            const todayStart = startOfDay(new Date());
            const termStart = lesson.Term?.startDate
              ? parseUTCDate(lesson.Term.startDate)
              : null;
            // A week is "past" once its final calendar day (its Sunday) has
            // elapsed. Monday-anchored weeks (termWeekAnchor) — matches backend.
            const isWeekPast = (week: number): boolean => {
              if (!termStart) return false;
              const weekEnd = termWeekAnchor(termStart);
              weekEnd.setUTCDate(weekEnd.getUTCDate() + (week - 1) * 7 + 6);
              return isBefore(weekEnd, todayStart);
            };

            // Get enrolled students for this lesson
            const enrolledStudents = lesson.LessonEnrollment || [];

            // The child this card books for, and THEIR OWN enrolment row: the start week
            // is per enrolment, so it is read from the row matching `studentId`.
            const studentId = lesson.LessonEnrollment?.[0]?.studentId;
            const childEnrollment = lesson.LessonEnrollment?.find((e) => e.studentId === studentId);
            const childName = childEnrollment?.Student?.firstName?.trim() || 'Your child';
            // The first week THIS child attends (class start vs their own) and the
            // class's last week. Missing data degrades to "no restriction".
            const childFirstWeek = effectiveStartWeek(lesson, childEnrollment);
            const classLastWeek =
              lesson.Term?.startDate && lesson.Term?.endDate
                ? lessonWeekWindow(
                    lesson,
                    termWeekCount(lesson.Term.startDate, lesson.Term.endDate)
                  ).last
                : null;
            // Why a week is not this child's (before their start / after the class ends),
            // or null. Shared by the Week Schedule chips and the Sessions to Book buttons.
            const outsideWindowReason = (week: number): string | null =>
              week < childFirstWeek
                ? `${childName} starts in Week ${childFirstWeek}`
                : classLastWeek != null && week > classLastWeek
                  ? `Class ends in Week ${classLastWeek}`
                  : null;
            const studentNames = enrolledStudents
              .map((enrollment) => enrollment.Student)
              .filter((student) => student)
              .map((student) => `${student.firstName} ${student.lastName}`)
              .join(', ');

            return (
              <Card key={lesson.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="h6">{lesson.name}</Typography>
                      {studentNames && (
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                          {studentNames}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={pattern.bookingsOpen ? 'Bookings Open' : 'Bookings Closed'}
                      color={pattern.bookingsOpen ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {lesson.Instructor?.User?.firstName} {lesson.Instructor?.User?.lastName} |{' '}
                    {lesson.Room?.Location?.name} - {lesson.Room?.name}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {!pattern.bookingsOpen && (() => {
                    const preBookedWeeks = lessonBookings
                      .filter((b) => b.isAdminPreBooking && b.status !== 'CANCELLED')
                      .map((b) => b.weekNumber);
                    return preBookedWeeks.length > 0 ? (
                      <Alert severity="success" icon={<LockIcon />} sx={{ mb: 2 }}>
                        Your child has reserved slots for week{preBookedWeeks.length > 1 ? 's' : ''}{' '}
                        {preBookedWeeks.join(', ')}. The school will confirm these when bookings open.
                      </Alert>
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Bookings are currently closed for this lesson. Please contact the school if you have any questions or need to make a booking.
                      </Alert>
                    );
                  })()}

                  <Typography variant="subtitle2" gutterBottom>
                    Week Schedule
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((week) => {
                      const isIndividual = individualWeeks.includes(week);
                      const isGroup = groupWeeks.includes(week);
                      // A week is "booked" if a booking RUNS in it (weekNumber)
                      // OR is FOR it as a clinic (clinicWeekNumber) — a moved
                      // open-week/overflow booking marks BOTH its clinic week
                      // and the week it was scheduled into.
                      const weekBooking = lessonBookings.find(
                        (b) =>
                          b.status !== 'CANCELLED' &&
                          (b.weekNumber === week || (b.clinicWeekNumber ?? b.weekNumber) === week)
                      );
                      const hasBooking = !!weekBooking;
                      const isPreBooked = weekBooking?.isAdminPreBooking === true;
                      // Clinic week whose session was moved elsewhere — say where.
                      const movedTo =
                        weekBooking && weekBooking.weekNumber !== week
                          ? weekBooking.weekNumber
                          : null;
                      // A week that is not this child's (before their start, after the class
                      // ends) is muted with the reason — never the "Individual (Book)" warning.
                      const notTheirs = hasBooking ? null : outsideWindowReason(week);

                      return (
                        <Chip
                          key={week}
                          label={`W${week}`}
                          size="small"
                          variant={notTheirs ? 'outlined' : isIndividual ? 'filled' : 'outlined'}
                          color={
                            hasBooking
                              ? 'success'
                              : notTheirs
                              ? 'default'
                              : isIndividual
                              ? 'warning'
                              : isGroup
                              ? 'primary'
                              : 'default'
                          }
                          icon={isPreBooked ? <LockIcon /> : hasBooking ? <CheckCircleIcon /> : undefined}
                          title={
                            isPreBooked
                              ? 'Reserved by school'
                              : movedTo
                                ? `Booked — runs in week ${movedTo}`
                                : notTheirs ?? undefined
                          }
                          data-testid={`week-chip-${week}`}
                          sx={{ minWidth: 60 }}
                        />
                      );
                    })}
                  </Stack>

                  <Box sx={{ display: 'flex', gap: 2, fontSize: '0.75rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip size="small" color="primary" variant="outlined" label="" sx={{ width: 16, height: 16 }} />
                      <Typography variant="caption">Group</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip size="small" color="warning" label="" sx={{ width: 16, height: 16 }} />
                      <Typography variant="caption">Individual (Book)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip size="small" color="success" label="" sx={{ width: 16, height: 16 }} />
                      <Typography variant="caption">Booked</Typography>
                    </Box>
                  </Box>

                  {/* Sessions to Book — one button per unbooked clinic week.
                      The booking modal's slot picker shows that clinic week's
                      slots and, when overflow is open, the following week's slots
                      below them — so there is no separate overflow button here. */}
                  {pattern.bookingsOpen && (() => {
                    // Books for `studentId` (the card's child, resolved above with their window).
                    if (!studentId) return null;
                    // A clinic week is "done" if the student has an active booking
                    // FOR it — resolve by clinic week (clinicWeekNumber ?? weekNumber)
                    // so an overflow booking (held in N+1) still counts as booking clinic N.
                    const isClinicBooked = (clinicWeek: number) =>
                      lessonBookings.some(
                        (b) =>
                          (b.clinicWeekNumber ?? b.weekNumber) === clinicWeek &&
                          b.status !== 'CANCELLED'
                      );
                    const sessionWeeks = individualWeeks.filter((week) => !isClinicBooked(week));
                    if (sessionWeeks.length === 0) return null;
                    return (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Sessions to Book
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {sessionWeeks.map((week) => {
                            // Once the clinic week itself has passed the whole
                            // session is closed — including overflow. Overflow only
                            // helps while the clinic week is still upcoming/current
                            // (when its slots fill up), not as a grace period after.
                            // EXCEPTION: an OPEN clinic week (admin freed it — e.g.
                            // the teacher was away that week) stays bookable into
                            // any remaining week of the term, even after its own
                            // scheduled week has passed.
                            const isOpenClinic = (
                              (pattern.openClinicWeeks ?? []) as number[]
                            ).includes(week);
                            const isPast = isWeekPast(week) && !isOpenClinic;
                            // Before the child's start (or after the class's last week) the
                            // button stays visible but dead, with the reason — never relaxed
                            // by an open clinic.
                            const windowReason = outsideWindowReason(week);
                            return (
                              <Button
                                key={week}
                                size="small"
                                variant="outlined"
                                color={isPast && !windowReason ? 'error' : 'primary'}
                                disabled={isPast || windowReason !== null}
                                startIcon={<EventIcon />}
                                onClick={() => handleOpenBookingModal(lesson, week, studentId)}
                                title={
                                  windowReason
                                    ? windowReason
                                    : isPast
                                    ? 'This week has already passed'
                                    : isOpenClinic
                                      ? 'This clinic is open — book any available week'
                                      : undefined
                                }
                              >
                                Week {week}
                              </Button>
                            );
                          })}
                        </Box>
                        {(() => {
                          // The disabled buttons' reasons are hover-only (`title`), which a
                          // phone never shows: say it once, visibly, under the buttons.
                          const termStart = lesson.Term?.startDate;
                          if (!termStart) return null;
                          const dated = (week: number) =>
                            formatTermWeekDate(termStart, week, lesson.dayOfWeek ?? null);
                          const sentences: string[] = [];
                          if (sessionWeeks.some((week) => week < childFirstWeek)) {
                            sentences.push(
                              `${childName} starts in ${dated(childFirstWeek)} — earlier clinics can't be booked.`
                            );
                          }
                          if (classLastWeek != null && sessionWeeks.some((week) => week > classLastWeek)) {
                            sentences.push(`${lesson.name}'s last week is ${dated(classLastWeek)}.`);
                          }
                          if (sentences.length === 0) return null;
                          return (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 1 }}
                              data-testid="booking-window-caption"
                            >
                              {sentences.join(' ')}
                            </Typography>
                          );
                        })()}
                      </>
                    );
                  })()}

                  {/* Booked individual sessions for this lesson */}
                  {(() => {
                    const activeBookings = lessonBookings
                      .filter((b) => b.status !== 'CANCELLED')
                      .sort(
                        (a, b) =>
                          parseUTCDate(a.scheduledDate).getTime() -
                          parseUTCDate(b.scheduledDate).getTime()
                      );
                    if (activeBookings.length === 0) return null;
                    return (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Booked Individual Sessions
                        </Typography>
                        <List dense disablePadding>
                          {activeBookings.map((booking) => {
                            const canModify = canModifyBooking(
                              booking.scheduledDate,
                              booking.startTime
                            );
                            const hoursUntil = getHoursUntilBooking(
                              booking.scheduledDate,
                              booking.startTime
                            );
                            // A booking whose clinic week is open stays
                            // parent-reschedulable even when it was admin
                            // pre-booked — the whole point of opening the
                            // week is the family re-picks.
                            const showActions =
                              canModify &&
                              booking.status !== 'COMPLETED' &&
                              (!booking.isAdminPreBooking || isClinicWeekOpen(booking));
                            return (
                              <ListItem
                                key={booking.id}
                                disableGutters
                                sx={{ py: 0.5 }}
                              >
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" fontWeight={600}>
                                      Week {booking.weekNumber}
                                    </Typography>
                                  }
                                  secondary={
                                    <>
                                      <Typography variant="body2" display="block">
                                        {format(parseUTCDate(booking.scheduledDate), 'EEE, d MMM')}
                                      </Typography>
                                      <Typography variant="body2" display="block">
                                        {formatTimeSlot(booking.startTime, booking.endTime)}
                                      </Typography>
                                      {booking.isAdminPreBooking && (
                                        <Box sx={{ mt: 0.5 }}>
                                          <Chip
                                            label="Reserved by school"
                                            size="small"
                                            icon={<LockIcon />}
                                            sx={{
                                              bgcolor: '#e8f5e9',
                                              color: '#2e7d32',
                                              fontSize: '0.65rem',
                                              height: 20,
                                            }}
                                          />
                                        </Box>
                                      )}
                                      {isClinicWeekOpen(booking) && (
                                        <Box sx={{ mt: 0.5 }}>
                                          <Chip
                                            label="Can move to any week"
                                            size="small"
                                            sx={{
                                              bgcolor: '#a3d9f6',
                                              color: '#1565c0',
                                              fontSize: '0.65rem',
                                              height: 20,
                                            }}
                                          />
                                        </Box>
                                      )}
                                      {!canModify && hoursUntil > 0 && !booking.isAdminPreBooking && (
                                        <Typography variant="caption" color="warning.main" display="block">
                                          <WarningIcon
                                            sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }}
                                          />
                                          {Math.floor(hoursUntil)}h until session - cannot modify
                                        </Typography>
                                      )}
                                    </>
                                  }
                                />
                                {showActions && (
                                  <ListItemSecondaryAction>
                                    <Tooltip title="Reschedule">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenRescheduleModal(booking)}
                                        aria-label="Reschedule booking"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleOpenCancelModal(booking)}
                                        aria-label="Cancel booking"
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemSecondaryAction>
                                )}
                              </ListItem>
                            );
                          })}
                        </List>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </Grid>

        {/* My Bookings Section - only show if not hidden */}
        {!hideBookingsList && (
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              My Bookings
            </Typography>

            {(!myBookings || myBookings.length === 0) && (
              <Alert severity="info">No bookings yet.</Alert>
            )}

            <List>
              {(myBookings ?? [])
                .filter((b) => b.status !== 'CANCELLED')
                .sort(
                  (a, b) =>
                    new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
                )
                .map((booking) => {
                  const canModify = canModifyBooking(booking.scheduledDate, booking.startTime);
                  const hoursUntil = getHoursUntilBooking(booking.scheduledDate, booking.startTime);

                  return (
                    <Paper key={booking.id} sx={{ mb: 1 }}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {booking.Lesson?.name || 'Lesson'}
                              <Chip
                                label={booking.status}
                                size="small"
                                color={getBookingStatusColor(booking.status)}
                              />
                              {booking.isAdminPreBooking && (
                                <Chip
                                  label="Reserved by school"
                                  size="small"
                                  icon={<LockIcon />}
                                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2">
                                {format(parseUTCDate(booking.scheduledDate), 'EEE, d MMM')} |{' '}
                                {formatTimeSlot(booking.startTime, booking.endTime)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Week {booking.weekNumber} |{' '}
                                {booking.Student?.firstName} {booking.Student?.lastName}
                              </Typography>
                              {!canModify && hoursUntil > 0 && !booking.isAdminPreBooking && (
                                <Typography variant="caption" color="warning.main" display="block">
                                  <WarningIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                  {Math.floor(hoursUntil)}h until session - cannot modify
                                </Typography>
                              )}
                            </>
                          }
                        />
                        {canModify && booking.status !== 'COMPLETED' && !booking.isAdminPreBooking && (
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenRescheduleModal(booking)}
                              title="Reschedule"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCancelModal(booking)}
                              color="error"
                              title="Cancel"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    </Paper>
                  );
                })}
            </List>
          </Grid>
        )}
      </Grid>

      {/* Booking Modal */}
      <Dialog
        open={bookingModal.open}
        onClose={handleCloseBookingModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Book Individual Session - Week {bookingModal.weekNumber}
        </DialogTitle>
        <DialogContent>
          {bookingModal.lesson && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">{bookingModal.lesson.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingModal.lesson.Instructor?.User?.firstName}{' '}
                {bookingModal.lesson.Instructor?.User?.lastName}
              </Typography>
            </Box>
          )}

          {(availableSlots ?? []).some((s) => s.isOverflow) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Overflow bookings are now available — you can also book a slot in the
              following week (shown below).
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Available Time Slots
          </Typography>

          <SlotPicker
            slots={availableSlots}
            selectedSlot={selectedSlot}
            onSlotSelect={setSelectedSlot}
            isLoading={slotsLoading}
            emptyMessage="No available slots for this week."
            confirmationPrefix="You are booking for"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBookingModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmBooking}
            disabled={!selectedSlot || createBooking.isPending}
          >
            {createBooking.isPending ? <CircularProgress size={20} /> : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog
        open={rescheduleModal.open}
        onClose={handleCloseRescheduleModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isOpenWeekReschedule
            ? `Reschedule Clinic ${rescheduleModal.booking?.clinicWeekNumber ?? rescheduleModal.booking?.weekNumber} — Any Week`
            : `Reschedule Booking - Week ${rescheduleModal.booking?.weekNumber}`}
        </DialogTitle>
        <DialogContent>
          {rescheduleModal.booking && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                {rescheduleModal.booking.Lesson?.name || 'Lesson'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current: {format(parseUTCDate(rescheduleModal.booking.scheduledDate), 'EEE, d MMM')} |{' '}
                {formatTimeSlot(rescheduleModal.booking.startTime, rescheduleModal.booking.endTime)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Select New Time Slot
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            {isOpenWeekReschedule
              ? 'The school has opened this clinic to any week — choose any available time in the term. Your clinic stays the same; only when it runs changes.'
              : 'Select a new day and time below. You can choose any available slot within the same week.'}
          </Alert>

          <SlotPicker
            slots={availableSlots}
            selectedSlot={selectedSlot}
            onSlotSelect={setSelectedSlot}
            isLoading={slotsLoading}
            emptyMessage={
              isOpenWeekReschedule
                ? 'No available slots in the remaining weeks of term.'
                : 'No other available slots for this week.'
            }
            confirmationPrefix="New time:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRescheduleModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!rescheduleModal.booking || !selectedSlot) return;
              const dateKey =
                selectedSlot.dateKey ??
                (selectedSlot.date
                  ? new Date(selectedSlot.date).toISOString().split('T')[0]
                  : '');
              rescheduleBooking.mutate(
                {
                  id: rescheduleModal.booking.id,
                  data: {
                    scheduledDate: `${dateKey}T00:00:00.000Z`,
                    startTime: selectedSlot.startTime,
                    endTime: selectedSlot.endTime,
                  },
                },
                {
                  onSuccess: () => {
                    handleCloseRescheduleModal();
                  },
                }
              );
            }}
            disabled={!selectedSlot || rescheduleBooking.isPending}
          >
            {rescheduleBooking.isPending ? <CircularProgress size={20} /> : 'Confirm Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelModal.open} onClose={handleCloseCancelModal}>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to cancel this booking?
          </Typography>
          {cancelModal.booking && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {cancelModal.booking.Lesson?.name} - Week {cancelModal.booking.weekNumber}
              <br />
              {format(parseUTCDate(cancelModal.booking.scheduledDate), 'EEE, d MMM')} |{' '}
              {formatTimeSlot(cancelModal.booking.startTime, cancelModal.booking.endTime)}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Reason (optional)"
            value={cancelModal.reason}
            onChange={(e) =>
              setCancelModal((prev) => ({ ...prev, reason: e.target.value }))
            }
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelModal}>Keep Booking</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={cancelBooking.isPending}
          >
            {cancelBooking.isPending ? <CircularProgress size={20} /> : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
